import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { DialogService } from '../dialog/dialog.service';
import { HttpErrorResponse } from '@angular/common/http';

/** How long the service should ignore refresh requests after refreshing */
const SESSION_REFRESH_LIMIT = 1000 * 60 * 1; // 1 minute

/** Time remaining before session extend dialog opens */
const SESSION_TIMEOUT_LIMIT = 1000 * 60 * 1; // 1 minute

/** Offset between actual JWT expiry and time remaining counter, to account for network latency */
const SESSION_TIMEOUT_OFFSET = 1000 * 30; // 30 seconds

/**
 * Session service handles timing related to user sessions. This
 * service does not handle the actual authentication of the user,
 * that is done in the AuthService
 */
@Injectable({
  providedIn: 'root'
})
export class SessionService {
  /** Observable that fires every second to time the session timeout */
  private _timer$: Observable<number> = interval(1000);

  /** Observable to stop the previous timers */
  private _stop = new Subject<void>();

  /** Representation of how much time is left in the session */
  private _timeLeftInSession: number = 0;
  private _clock = new BehaviorSubject<string>("0:00");
  public clock$ = this._clock.asObservable();

  /** Observable of when the user refreshes */
  private _isRefreshingToken: boolean = false;
  private _lastRefreshedTimer: number = 0;

  /** Whether or not the dialog is open already */
  private _dialogOpen: boolean = false;

  /**
   * Default constructor for the session service which initializes a timer
   */
  constructor(
    private _authService: AuthService,
    private _dialogService: DialogService,
  ) {
    // Reset timer on authentication changes
    this._authService.authenticated$.subscribe(
      (authenticated: boolean) => {
        if (authenticated) {
          // If the user ever becomes authenticated, reset the clock
          console.log("[SESSION]: User authenticated, resetting clock");
          this._lastRefreshedTimer = 0; // Reset the timer for the next refresh
          this._resetClock();
        }
      }
    );

    // If the user refreshes the token by interacting with the page, reset the clock
    this._authService.refreshInProgress$.subscribe(() => {
      console.log("[SESSION]: User is refreshing token, resetting clock");
      this._resetClock()
    });

    // Subscribe to timer changes to control session dialog opening
    this._timer$.subscribe(
      (_: number) => {
        // If:
        // - The document is hidden (e.g. user switched tabs)
        // - The dialog is already open
        // - The user is not authenticated
        // - The time left in session is greater than the timeout limit
        // - The user is refreshing the token
        // - The time left in session is below zero (will log out)
        if (document.hidden || this._dialogOpen || !this._authService.isAuthenticated || this._timeLeftInSession > SESSION_TIMEOUT_LIMIT || this._isRefreshingToken || this._timeLeftInSession <= 0) {
          // Do not open dialog
          return;
        }

        // Open the dialog to extend session
        this._dialogOpen = true;
        this._lastRefreshedTimer = SESSION_REFRESH_LIMIT + 1000; // Set last refreshed timer to be above limit to prevent dialog opening while can't refresh
        console.log("[SESSION]: Time left in session is below warning limit, opening extend session dialog.");
        this._dialogService.openExtendSession().subscribe(() => this._dialogOpen = false);
      }
    );

    // When the user switches tabs, check if the session has expired
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        const newTimeLeft = this._calculateExpiryTime(this._authService.token || "");
        this._lastRefreshedTimer += this._timeLeftInSession - newTimeLeft;

        this._timeLeftInSession = newTimeLeft;
        console.log("[SESSION]: Document is visible again, recalculating time left in session:", this._timeLeftInSession);
      }
    });
  }

  /**
   * Refresh the session timer by obtaining a new token and resetting the clock
   */
  public refresh() {
    // Skip if the refresh timer is below the limit
    if (this._lastRefreshedTimer < SESSION_REFRESH_LIMIT) {
      console.log("[SESSION]: Skipping refresh");
      return;
    }

    // Skip if we are already refreshing the token
    if (this._isRefreshingToken) {
      console.log("[SESSION]: Already refreshing token, skipping refresh");
      return;
    }

    console.log("[SESSION]: Refreshing session timer...");

    this._isRefreshingToken = true;
    this._authService.refreshToken().subscribe({
      // On successful token refresh, reset the clock
      next: () => {
        console.log("[SESSION]: Token refreshed successfully.");
        this._isRefreshingToken = false;
        this._resetClock();
      },
      // If there is an error refreshing the token, log out the user
      error: (err: HttpErrorResponse) => {
        console.error("[SESSION]: Error refreshing token:", err);
        this._isRefreshingToken = false;
        this._authService.logout();
      }
    })
  }

  /**
   * Refresh the session clock
   */
  private async _resetClock() {
    // Stop other timers
    this._stop.next();

    // Get the user's token
    const token = this._authService.token;
    if (token == undefined || token === "") return;

    this._timeLeftInSession = this._calculateExpiryTime(token);

    // Calculate minutes and seconds
    const initial = this._timeLeftInSession / 1000; // Divide by milliseconds
    let minutes = Math.floor(initial / 60);
    let seconds = initial % 60;

    // Subscribe to clock changes
    const sub = this._timer$.subscribe(
      () => {
        this._lastRefreshedTimer += 1000;

        // If the clock is paused, do not decrement time left
        if (this._isRefreshingToken) {
          console.log("[SESSION]: Clock is paused, not decrementing time left");
          return;
        }

        // Decrement time left
        this._timeLeftInSession -= 1000;

        // Decrement seconds and minutes
        seconds = this._timeLeftInSession / 1000 % 60;
        minutes = Math.floor(this._timeLeftInSession / 1000 / 60);

        // Update timer
        this._clock.next(`${minutes}:${("" + seconds).length === 1 ? "0" : ""}${seconds}`);

        // If no time is left, unsubscribe from the timer and log out
        if (this._timeLeftInSession <= 0) {
          this._authService.logout();
          sub.unsubscribe();
        }
      });

    // Subscribe to the stop timer if any other refreshes occur
    this._stop.subscribe(
      () => {
        sub.unsubscribe();
      }
    );

    // Subscribe to the authentication status if the user logs out
    this._authService.authenticated$.subscribe(
      (authenticated: boolean) => {
        if (!authenticated) sub.unsubscribe();
      }
    );
  }

  /**
   * Calculate expiry time based on the JWT token
   * @param token The JWT token to calculate the expiry time from
   */
  private _calculateExpiryTime(token: string): number {
    if (token == undefined || token === "") return 0;

    // Calculate time left in session from the user's token
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      console.log("[SESSION]: Decoded token issued at:", decoded.iat);
      console.log("[SESSION]: Decoded token expiration:", decoded.exp);

      // Calculate time left in session and truncate to seconds
      const timeLeft = Math.floor((decoded.exp - decoded.iat) * 1000 - SESSION_TIMEOUT_OFFSET);
      console.log("[SESSION]: Time left in session:", this._timeLeftInSession);
      return timeLeft;
    } catch (error) {
      // Catch errors and result to default timeout interval
      console.error("[SESSION]: Error decoding token:", error);
      throw new Error("Invalid token format");
    }
  }

  /** Getters */
  public get dialogOpen(): boolean {
    return this._dialogOpen;
  }
}

